import React, { useEffect, useState } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  CircularProgress,
  Chip,
  Grid,
  LinearProgress,
  Paper,
  Stack,
  Container,
  Alert,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Badge,
  Divider,
} from "@mui/material";
import PowerIcon from "@mui/icons-material/Power";
import TimerIcon from "@mui/icons-material/Timer";
import CurrencyRupeeIcon from "@mui/icons-material/CurrencyRupee";
import TuneIcon from "@mui/icons-material/Tune";
import LaunchIcon from "@mui/icons-material/Launch";
import SearchIcon from "@mui/icons-material/Search";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import GradeIcon from "@mui/icons-material/Grade";
import CalculateIcon from "@mui/icons-material/Calculate";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import RefreshIcon from "@mui/icons-material/Refresh";
import BatteryChargingFullIcon from "@mui/icons-material/BatteryChargingFull";
import InfoIcon from "@mui/icons-material/Info";
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";
import ElectricBoltIcon from "@mui/icons-material/ElectricBolt";
import SummarizeIcon from "@mui/icons-material/Summarize";
import { getInverterRecommendations } from '../api/openai';

const Recommendation = ({ load = 800, backupTime = 4, budget = 15000, inverterType = "pure_sine" }) => {
  const [recommendations, setRecommendations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchProgress, setSearchProgress] = useState("");
  const [progressValue, setProgressValue] = useState(0);
  const [totalInventory, setTotalInventory] = useState(0);
  const [totalBatteries, setTotalBatteries] = useState(0);
  const [batteryAdvice, setBatteryAdvice] = useState("");
  const [batteryRecommendations, setBatteryRecommendations] = useState([]);
  const [aiProcessed, setAiProcessed] = useState(false);
  const [calculatedRequirements, setCalculatedRequirements] = useState(null);
  const [error, setError] = useState(false);

  const cleanMarkdown = (text) => {
    if (!text) return text;
    return text
      .replace(/\*\*(.*?)\*\*/g, '$1')
      .replace(/\*(.*?)\*/g, '$1')
      .replace(/__(.*?)__/g, '$1')
      .replace(/_(.*?)_/g, '$1')
      .replace(/`(.*?)`/g, '$1')
      .replace(/\[(.*?)\]\(.*?\)/g, '$1')
      .replace(/#+\s*/g, '')
      .replace(/^\s*[-*+]\s*/gm, '')
      .replace(/^\s*\d+\.\s*/gm, '')
      .trim();
  };

  const cleanFeatureArray = (features) => {
    if (!features) return ["Standard features"];
    if (Array.isArray(features)) {
      return features.map(feature => cleanMarkdown(feature));
    }
    if (typeof features === 'string') {
      return [cleanMarkdown(features)];
    }
    return ["Standard features"];
  };

  // Extract numeric price for sorting and calculations
  const extractPrice = (priceString) => {
    if (!priceString) return 999999;
    const matches = priceString.match(/₹?[\d,]+/);
    if (matches) {
      return parseInt(matches[0].replace(/[₹,]/g, ''));
    }
    return 999999;
  };

  // Get cost summary data
  const getCostSummary = () => {
    if (!recommendations.length || !batteryRecommendations.length) return null;

    const bestInverter = recommendations[0]; // Already sorted by price
    const bestBattery = batteryRecommendations[0];

    const inverterPrice = extractPrice(bestInverter.estimatedPrice);
    const batteryPrice = extractPrice(bestBattery.price || "₹8000"); // Default battery price if not available

    return {
      inverter: {
        name: bestInverter.name,
        price: inverterPrice === 999999 ? "Price on request" : `₹${inverterPrice.toLocaleString()}`,
        vaRating: bestInverter.vaRating
      },
      battery: {
        name: bestBattery.name,
        price: batteryPrice === 999999 ? "₹8000" : `₹${batteryPrice.toLocaleString()}`,
        capacity: `${bestBattery.capacity}Ah`
      },
      total: inverterPrice !== 999999 && batteryPrice !== 999999 ? 
        `₹${(inverterPrice + batteryPrice).toLocaleString()}` : 
        "Contact for pricing"
    };
  };

  const CostSummary = () => {
    const summary = getCostSummary();
    
    if (!summary) return null;

    return (
      <Paper elevation={3} sx={{ 
        mb: 4, 
        borderRadius: 3, 
        overflow: "hidden",
        border: "2px solid rgba(9, 135, 66, 0.79)"
      }}>
        <Box sx={{ 
          bgcolor: "#000000", 
          color: "white", 
          p: 3,
          display: "flex",
          alignItems: "center",
          gap: 2
        }}>
          <SummarizeIcon sx={{ color: "white", fontSize: 28 }} />
          <Typography variant="h5" fontWeight="700" sx={{ 
            fontFamily: "Noto Sans, sans-serif"
          }}>
            Recommended Solution Summary
          </Typography>
        </Box>
        
        <CardContent sx={{ p: 4 }}>
          <Grid container spacing={4} alignItems="center">
            {/* Inverter Summary */}
            <Grid item xs={12} md={4}>
              <Paper elevation={1} sx={{ 
                p: 3, 
                height: "100%",
                bgcolor: "rgba(9, 135, 66, 0.05)", 
                border: "1px solid rgba(9, 135, 66, 0.79)",
                borderRadius: 2
              }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
                  <ElectricBoltIcon sx={{ color: "rgba(9, 135, 66, 0.79)" }} />
                  <Typography variant="h6" fontWeight="600" color="rgba(9, 135, 66, 0.79)" sx={{ 
                    fontFamily: "Noto Sans, sans-serif" 
                  }}>
                    Best Inverter
                  </Typography>
                </Box>
                <Typography variant="body1" fontWeight="600" sx={{ 
                  mb: 1,
                  fontFamily: "Noto Sans, sans-serif",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap"
                }}>
                  {summary.inverter.name}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ 
                  mb: 2, 
                  fontFamily: "Noto Sans, sans-serif" 
                }}>
                  {summary.inverter.vaRating}
                </Typography>
                <Typography variant="h6" fontWeight="700" color="rgba(9, 135, 66, 0.79)" sx={{ 
                  fontFamily: "Noto Sans, sans-serif" 
                }}>
                  {summary.inverter.price}
                </Typography>
              </Paper>
            </Grid>

            {/* Battery Summary */}
            <Grid item xs={12} md={4}>
              <Paper elevation={1} sx={{ 
                p: 3, 
                height: "100%",
                bgcolor: "rgba(0, 0, 0, 0.05)", 
                border: "1px solid #000000",
                borderRadius: 2
              }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
                  <BatteryChargingFullIcon sx={{ color: "#000000" }} />
                  <Typography variant="h6" fontWeight="600" color="#000000" sx={{ 
                    fontFamily: "Noto Sans, sans-serif" 
                  }}>
                    Best Battery
                  </Typography>
                </Box>
                <Typography variant="body1" fontWeight="600" sx={{ 
                  mb: 1,
                  fontFamily: "Noto Sans, sans-serif",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap"
                }}>
                  {summary.battery.name}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ 
                  mb: 2, 
                  fontFamily: "Noto Sans, sans-serif" 
                }}>
                  {summary.battery.capacity}
                </Typography>
                <Typography variant="h6" fontWeight="700" color="#000000" sx={{ 
                  fontFamily: "Noto Sans, sans-serif" 
                }}>
                  {summary.battery.price}
                </Typography>
              </Paper>
            </Grid>

            {/* Total Cost */}
            <Grid item xs={12} md={4}>
              <Paper elevation={2} sx={{ 
                p: 3, 
                height: "100%",
                bgcolor: "linear-gradient(135deg, rgba(9, 135, 66, 0.79) 0%, #000000 100%)",
                background: "linear-gradient(135deg, rgba(9, 135, 66, 0.79) 0%, #000000 100%)",
                color: "white",
                borderRadius: 2
              }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
                  <CurrencyRupeeIcon sx={{ color: "white" }} />
                  <Typography variant="h6" fontWeight="600" color="white" sx={{ 
                    fontFamily: "Noto Sans, sans-serif" 
                  }}>
                    Total Investment
                  </Typography>
                </Box>
                <Typography variant="body2" sx={{ 
                  mb: 2, 
                  fontFamily: "Noto Sans, sans-serif",
                  opacity: 0.9
                }}>
                  Complete Setup Cost
                </Typography>
                <Typography variant="h4" fontWeight="700" color="white" sx={{ 
                  fontFamily: "Noto Sans, sans-serif",
                  textShadow: "0 2px 4px rgba(0,0,0,0.3)"
                }}>
                  {summary.total}
                </Typography>
                {summary.total !== "Contact for pricing" && (
                  <Typography variant="body2" sx={{ 
                    mt: 1,
                    fontFamily: "Noto Sans, sans-serif",
                    opacity: 0.8
                  }}>
                    + Installation & accessories
                  </Typography>
                )}
              </Paper>
            </Grid>
          </Grid>

          {/* Budget Comparison */}
          {summary.total !== "Contact for pricing" && (
            <Box sx={{ mt: 3 }}>
              <Divider sx={{ mb: 2 }} />
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <Typography variant="body2" color="text.secondary" sx={{ 
                  fontFamily: "Noto Sans, sans-serif" 
                }}>
                  Budget Comparison:
                </Typography>
                <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                  <Typography variant="body2" sx={{ fontFamily: "Noto Sans, sans-serif" }}>
                    Your Budget: ₹{budget.toLocaleString()}
                  </Typography>
                  {extractPrice(summary.total) <= budget ? (
                    <Chip 
                      icon={<CheckCircleIcon />} 
                      label="Within Budget" 
                      color="success" 
                      size="small"
                      sx={{ fontFamily: "Noto Sans, sans-serif" }}
                    />
                  ) : (
                    <Chip 
                      icon={<InfoIcon />} 
                      label={`₹${(extractPrice(summary.total) - budget).toLocaleString()} over budget`}
                      color="warning" 
                      size="small"
                      sx={{ fontFamily: "Noto Sans, sans-serif" }}
                    />
                  )}
                </Box>
              </Box>
            </Box>
          )}
        </CardContent>
      </Paper>
    );
  };

  const SimpleBatteryCard = ({ battery, calculatedRequirements }) => {
    const isOptimal = calculatedRequirements ? 
      battery.capacity >= calculatedRequirements.requiredBatteryAh : true;
    
    const hasValidLinks = (
      (battery.links?.amazon && battery.links.amazon.trim() !== '' && battery.links.amazon !== 'null') ||
      (battery.links?.flipkart && battery.links.flipkart.trim() !== '' && battery.links.flipkart !== 'null') ||
      (battery.links?.brand && battery.links.brand.trim() !== '' && battery.links.brand !== 'null')
    );

    return (
    <Card 
  elevation={3} 
  sx={{ 
    height: "100%", 
    minHeight: "600px",
    width: "100%", 
    display: "flex", 
    flexDirection: "column",
    borderRadius: 3, 
    border: `2px solid ${isOptimal ? "rgba(9, 135, 66, 0.79)" : "#e5e7eb"}`,
    backgroundColor: "white",
    position: "relative",
    overflow: "visible",
    maxWidth: "none", 
    flex: "1 1 auto" 
  }}
>
        {/* Top Badge - matching inverter style */}
        <Box sx={{ position: "absolute", top: -10, left: 20, zIndex: 1 }}>
          <Chip 
            icon={<BatteryChargingFullIcon sx={{ color: "white" }} />} 
            label={`Battery ${battery.displayIndex || battery.id || 'Option'}`} 
            sx={{ 
              bgcolor: isOptimal ? "rgba(9, 135, 66, 0.79)" : "#000000", 
              color: "white", 
              fontWeight: "bold", 
              fontSize: "0.85rem", 
              fontFamily: "Noto Sans, sans-serif", 
              "& .MuiChip-icon": { color: "white !important", fontSize: "1rem" } 
            }} 
          />
        </Box>

        {/* Header with same styling and height as inverter cards */}
        <Box sx={{ 
          bgcolor: "rgba(9, 135, 66, 0.79)", 
          color: "white", 
          p: 3,
          pt: 4, // Extra padding top for badge
          minHeight: "120px", // Match inverter card header height
          display: "flex",
          flexDirection: "column",
          justifyContent: "center"
        }}>
          <Typography 
            variant="h6" 
            fontWeight="700" 
            sx={{ 
              mb: 1, 
              fontFamily: "Noto Sans, sans-serif",
              fontSize: "1.25rem", // Match inverter title size
              lineHeight: 1.3,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap"
            }}
          >
            {battery.name}
          </Typography>
          <Typography 
            variant="body1" 
            sx={{ 
              opacity: 0.9, 
              fontFamily: "Noto Sans, sans-serif",
              fontSize: "1rem" // Match inverter brand text size
            }}
          >
            {battery.brand || (battery.name?.split(' ')[0]) || 'Premium Battery'}
          </Typography>
        </Box>

        {/* Content area matching inverter card structure */}
        <CardContent sx={{ flexGrow: 1, p: 3, display: "flex", flexDirection: "column" }}>
          {/* Specifications Grid - matching inverter layout */}
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={6}>
              <Paper elevation={1} sx={{ 
                p: 2, 
                textAlign: "center", 
                bgcolor: "white", 
                border: "1px solid rgba(9, 135, 66, 0.79)",
                borderRadius: 1
              }}>
                <Typography variant="body2" color="rgba(9, 135, 66, 0.79)" sx={{ fontFamily: "Noto Sans, sans-serif" }}>
                  Capacity
                </Typography>
                <Typography variant="h6" fontWeight="600" color="rgba(9, 135, 66, 0.79)" sx={{ fontFamily: "Noto Sans, sans-serif" }}>
                  {battery.capacity}Ah
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={6}>
              <Paper elevation={1} sx={{ 
                p: 2, 
                textAlign: "center", 
                bgcolor: "white", 
                border: "1px solid #000000",
                borderRadius: 1
              }}>
                <Typography variant="body2" color="#000000" sx={{ fontFamily: "Noto Sans, sans-serif" }}>
                  Type
                </Typography>
                <Typography variant="h6" fontWeight="600" color="#000000" sx={{ fontFamily: "Noto Sans, sans-serif" }}>
                  {battery.type || battery.battery_type || 'Tubular'}
                </Typography>
              </Paper>
            </Grid>
          </Grid>

          {/* Compatibility Info - matching inverter style */}
          <Paper elevation={0} sx={{ 
            p: 2, 
            mb: 3, 
            bgcolor: "#f9f9f9", 
            border: "1px solid #e5e7eb", 
            borderRadius: 2
          }}>
            <Typography variant="subtitle2" fontWeight="600" color="#000000" sx={{ fontFamily: "Noto Sans, sans-serif", mb: 1 }}>
              Inverter Compatibility
            </Typography>
            <Typography variant="body2" color="#000000" sx={{ fontFamily: "Noto Sans, sans-serif" }}>
              Compatible with {battery.invRatingStart || battery.inv_rating_start}VA - {battery.invRatingEnd || battery.inv_rating_end}VA inverters
            </Typography>
          </Paper>

          {/* Requirement Match Status */}
          {calculatedRequirements && (
            <Paper elevation={0} sx={{ 
              p: 2, 
              bgcolor: "white", 
              border: `1px solid ${isOptimal ? "rgba(9, 135, 66, 0.79)" : "#e5e7eb"}`, 
              borderRadius: 2,
              mb: 3
            }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
                {isOptimal ? 
                  <CheckCircleIcon sx={{ color: "rgba(9, 135, 66, 0.79)", fontSize: 18 }} /> :
                  <InfoIcon sx={{ color: "#000000", fontSize: 18 }} />
                }
                <Typography variant="subtitle2" fontWeight="600" color={isOptimal ? "rgba(9, 135, 66, 0.79)" : "#000000"} sx={{ fontFamily: "Noto Sans, sans-serif" }}>
                  Requirement Analysis
                </Typography>
              </Box>
              <Typography variant="body2" color={isOptimal ? "rgba(9, 135, 66, 0.79)" : "#000000"} sx={{ fontStyle: "italic", fontFamily: "Noto Sans, sans-serif" }}>
                {isOptimal ? 
                  `✓ Exceeds required ${calculatedRequirements.requiredBatteryAh}Ah capacity` :
                  `⚠ Closest available to ${calculatedRequirements.requiredBatteryAh}Ah requirement`
                }
              </Typography>
            </Paper>
          )}

          {/* Spacer to push purchase links to bottom */}
          <Box sx={{ flexGrow: 1 }} />
        </CardContent>

        {/* Purchase Links Section - matching inverter structure and positioned at bottom */}
        <Box sx={{ p: 3, pt: 0, mt: "auto" }}>
          <Typography variant="subtitle2" fontWeight="600" sx={{ 
            mb: 2, 
            color: "#000000", 
            fontFamily: "Noto Sans, sans-serif",
            display: "flex",
            alignItems: "center",
            gap: 1
          }}>
            <ShoppingCartIcon sx={{ fontSize: 18 }} />
            Battery Purchase Links
          </Typography>
          
          <Stack spacing={1.5}>
            {/* Amazon Button */}
            <Button
              variant="contained"
              startIcon={<LaunchIcon />}
              href={hasValidLinks && battery.links?.amazon ? battery.links.amazon : `https://www.amazon.in/s?k=${encodeURIComponent(battery.name)}`}
              target="_blank"
              rel="noopener noreferrer"
              fullWidth
  sx={{
  width: { xs: 400, sm: 350, md: 400, lg: 450 }, 
  maxWidth: "100%", 
  py: 1.2,
  fontWeight: 600,
  borderRadius: 2,
  bgcolor: "rgba(9, 135, 66, 0.79)",
  fontFamily: "Noto Sans, sans-serif",
  textTransform: "none",
  "&:hover": {
    bgcolor: "#16a34a",
  },
}}

            >
              {hasValidLinks && battery.links?.amazon ?
                `Buy Battery on Amazon` :
                `Search Battery on Amazon`
              }
            </Button>

            {/* Flipkart Button */}
            <Button
              variant="outlined"
              startIcon={<LaunchIcon />}
              href={hasValidLinks && battery.links?.flipkart ? battery.links.flipkart : `https://www.flipkart.com/search?q=${encodeURIComponent(battery.name)}`}
              target="_blank"
              rel="noopener noreferrer"
              fullWidth
              sx={{
                borderRadius: 2,
                borderColor: "#000000",
                color: "#000000",
                fontFamily: "Noto Sans, sans-serif",
                textTransform: "none",
                "&:hover": { 
                  bgcolor: "#000000", 
                  color: "white"
                }
              }}
            >
              {hasValidLinks && battery.links?.flipkart ?
                `Buy Battery on Flipkart` :
                `Search Battery on Flipkart`
              }
            </Button>

            {/* Brand Store Button */}
            {hasValidLinks && battery.links?.brand && (
              <Button
                variant="outlined"
                startIcon={<LaunchIcon />}
                href={battery.links.brand}
                target="_blank"
                rel="noopener noreferrer"
                fullWidth
                sx={{
                  borderRadius: 2,
                  borderColor: "rgba(9, 135, 66, 0.79)",
                  color: "rgba(9, 135, 66, 0.79)",
                  fontFamily: "Noto Sans, sans-serif",
                  textTransform: "none",
                  "&:hover": { 
                    bgcolor: "rgba(9, 135, 66, 0.79)", 
                    color: "white"
                  }
                }}
              >
                Buy from Brand Store
              </Button>
            )}
          </Stack>
        </Box>
      </Card>
    );
  };

  useEffect(() => {
    const fontStyle = document.createElement("style");
    fontStyle.textContent = `
      .MuiTypography-root,
      .MuiButton-root,
      .MuiChip-root,
      .recommendation-component * {
        font-family: 'Noto Sans', sans-serif !important;
      }
    `;
    document.head.appendChild(fontStyle);
    return () => {
      if (document.head.contains(fontStyle)) {
        document.head.removeChild(fontStyle);
      }
    };
  }, []);

  useEffect(() => {
    fetchRecommendations();
    // eslint-disable-next-line
  }, [load, backupTime, budget, inverterType]);

  const fetchRecommendations = async () => {
    setLoading(true);
    setError(false);
    setProgressValue(0);
    setSearchProgress("Initializing electrical calculations...");
    try {
      setProgressValue(15);
      setSearchProgress("Calculating VA and battery requirements...");
      await new Promise((resolve) => setTimeout(resolve, 800));
      setProgressValue(30);
      setSearchProgress("Fetching inverter database from webhook...");
      await new Promise((resolve) => setTimeout(resolve, 800));
      setProgressValue(45);
      setSearchProgress("Loading battery compatibility database...");
      await new Promise((resolve) => setTimeout(resolve, 800));
      setProgressValue(60);
      setSearchProgress("Matching inverters with VA rating requirements...");
      const result = await getInverterRecommendations(load, backupTime, budget, inverterType);
      setProgressValue(75);
      setSearchProgress("AI analyzing battery compatibility...");
      await new Promise((resolve) => setTimeout(resolve, 1000));
      setProgressValue(90);
      setSearchProgress("Generating personalized recommendations...");

      if (result.success && result.recommendations) {
        const { recommendations: recData, calculatedRequirements: calcReqs } = result;
        let finalRecommendations = [];
        if (recData.inverters && Array.isArray(recData.inverters)) {
          finalRecommendations = recData.inverters.map((rec, index) => ({
            name: cleanMarkdown(rec.name) || `Inverter Model ${index + 1}`,
            brand: cleanMarkdown(rec.name?.split(' ')[0]) || "Brand",
            vaRating: cleanMarkdown(rec.vaRating) || "Not specified",
            estimatedPrice: cleanMarkdown(rec.priceAnalysis) || "Contact for pricing",
            suitability: cleanMarkdown(rec.suitability) || "Suitable for your requirements",
            keyFeatures: cleanFeatureArray(rec.keyFeatures),
            batteryRecommendation: cleanMarkdown(rec.batteryRecommendation) || "Standard battery configuration",
            links: rec.links || {}
          }));

          // Sort inverters by price (cheapest first)
          finalRecommendations.sort((a, b) => {
            const priceA = extractPrice(a.estimatedPrice);
            const priceB = extractPrice(b.estimatedPrice);
            return priceA - priceB;
          });

          setBatteryAdvice(cleanMarkdown(recData.batteryAdvice) || "");
          if (recData.batteryRecommendations && Array.isArray(recData.batteryRecommendations)) {
            const processedBatteryRecs = recData.batteryRecommendations.map((battery, index) => ({
              ...battery,
              displayIndex: battery.index || index + 1,
              links: {
                amazon: (battery.amazon_link && battery.amazon_link !== 'null' && battery.amazon_link.trim() !== '') ? battery.amazon_link : "",
                flipkart: (battery.flipkart_link && battery.flipkart_link !== 'null' && battery.flipkart_link.trim() !== '') ? battery.flipkart_link : "",
                brand: (battery.brand_buy_link && battery.brand_buy_link !== 'null' && battery.brand_buy_link.trim() !== '') ? battery.brand_buy_link : ""
              },
              invRatingStart: battery.inv_rating_start,
              invRatingEnd: battery.inv_rating_end,
              type: battery.battery_type
            }));
            setBatteryRecommendations(processedBatteryRecs);
          } else {
            setBatteryRecommendations([]);
          }
          setAiProcessed(recData.aiProcessed || false);
          setCalculatedRequirements(calcReqs || null);
        }
        setRecommendations(finalRecommendations);
        setTotalInventory(result.totalInvertersChecked || 0);
        setTotalBatteries(result.totalBatteriesChecked || 0);
        setProgressValue(100);
        setSearchProgress("Complete! Found optimal matches with battery compatibility!");
      } else {
        throw new Error(result.error || "Failed to get recommendations");
      }
    } catch (err) {
      setError(true);
      setSearchProgress("Error loading recommendations");
      setRecommendations([]);
      setProgressValue(100);
    }
    setTimeout(() => {
      setLoading(false);
      setSearchProgress("");
    }, 1000);
  };

  const handleRetry = () => {
    fetchRecommendations();
  };

  if (loading) {
    return (
      <Container maxWidth="md" sx={{ py: 8 }} className="recommendation-component">
        <Paper elevation={0} sx={{ p: 6, textAlign: "center", borderRadius: 3, bgcolor: "white", border: "1px solid rgba(9, 135, 66, 0.79)" }}>
          <CircularProgress size={80} sx={{ mb: 3, color: "rgba(9, 135, 66, 0.79)", "& .MuiCircularProgress-circle": { strokeLinecap: "round" } }} />
          <Typography variant="h5" fontWeight="600" sx={{ mb: 2, color: "rgba(9, 135, 66, 0.79)", fontFamily: "Noto Sans, sans-serif" }}>
            {cleanMarkdown(searchProgress) || "Processing electrical calculations..."}
          </Typography>
          <Box sx={{ maxWidth: 400, mx: "auto", mb: 3 }}>
            <LinearProgress variant="determinate" value={progressValue} sx={{ height: 10, borderRadius: 5, bgcolor: "#e5e7eb", "& .MuiLinearProgress-bar": { borderRadius: 5, bgcolor: "rgba(9, 135, 66, 0.79)" } }} />
            <Typography variant="body2" color="rgba(9, 135, 66, 0.79)" sx={{ mt: 1, fontFamily: "Noto Sans, sans-serif" }}>
              {progressValue}% Complete
            </Typography>
          </Box>
          <Stack direction="row" spacing={2} justifyContent="center" alignItems="center" sx={{ mb: 3 }}>
            <CalculateIcon sx={{ color: "rgba(9, 135, 66, 0.79)" }} />
            <Typography variant="body1" color="rgba(9, 135, 66, 0.79)" sx={{ fontFamily: "Noto Sans, sans-serif" }}>
              Analyzing inverter & battery compatibility
            </Typography>
          </Stack>
        </Paper>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }} className="recommendation-component">
      {/* Cost Summary Section - NEW */}
      <CostSummary />

      <Paper elevation={2} sx={{ mb: 4, borderRadius: 3, overflow: "hidden" }}>
        <Box sx={{ bgcolor: "rgba(9, 135, 66, 0.79)", color: "white", p: 3 }}>
          <Typography variant="h6" fontWeight="600" sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2, fontFamily: "Noto Sans, sans-serif" }}>
            <TuneIcon sx={{ color: "white" }} />
            Your Requirements & Electrical Calculations
          </Typography>
          <Grid container spacing={3}>
            <Grid item xs={6} md={3}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <PowerIcon sx={{ color: "white" }} />
                <Box>
                  <Typography variant="body2" sx={{ opacity: 0.9, fontFamily: "Noto Sans, sans-serif" }}>Power Load</Typography>
                  <Typography variant="h6" fontWeight="700" sx={{ fontFamily: "Noto Sans, sans-serif" }}>{load}W</Typography>
                </Box>
              </Box>
            </Grid>
            <Grid item xs={6} md={3}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <TimerIcon sx={{ color: "white" }} />
                <Box>
                  <Typography variant="body2" sx={{ opacity: 0.9, fontFamily: "Noto Sans, sans-serif" }}>Backup Time</Typography>
                  <Typography variant="h6" fontWeight="700" sx={{ fontFamily: "Noto Sans, sans-serif" }}>{backupTime} hours</Typography>
                </Box>
              </Box>
            </Grid>
            <Grid item xs={6} md={3}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <CurrencyRupeeIcon sx={{ color: "white" }} />
                <Box>
                  <Typography variant="body2" sx={{ opacity: 0.9, fontFamily: "Noto Sans, sans-serif" }}>Budget</Typography>
                  <Typography variant="h6" fontWeight="700" sx={{ fontFamily: "Noto Sans, sans-serif" }}>₹{budget.toLocaleString()}</Typography>
                </Box>
              </Box>
            </Grid>
            <Grid item xs={6} md={3}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <CheckCircleIcon sx={{ color: "white" }} />
                <Box>
                  <Typography variant="body2" sx={{ opacity: 0.9, fontFamily: "Noto Sans, sans-serif" }}>Wave Type</Typography>
                  <Typography variant="h6" fontWeight="700" sx={{ fontFamily: "Noto Sans, sans-serif" }}>
                    {inverterType === "pure_sine" ? "Pure Sine" : "Square Wave"}
                  </Typography>
                </Box>
              </Box>
            </Grid>
          </Grid>
          {calculatedRequirements && (
            <Paper sx={{ mt: 3, p: 2, bgcolor: "rgba(255,255,255,0.1)", borderRadius: 2 }}>
              <Typography variant="body2" sx={{ color: "white", mb: 1, fontWeight: 600, fontFamily: "Noto Sans, sans-serif" }}>
                Calculated Requirements:
              </Typography>
              <Stack direction="row" spacing={4}>
                <Box>
                  <Typography variant="body2" sx={{ color: "white", opacity: 0.9, fontFamily: "Noto Sans, sans-serif" }}>
                    Required VA: <strong>{calculatedRequirements.requiredVA}VA</strong>
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="body2" sx={{ color: "white", opacity: 0.9, fontFamily: "Noto Sans, sans-serif" }}>
                    Battery Capacity: <strong>{calculatedRequirements.requiredBatteryAh}Ah</strong>
                  </Typography>
                </Box>
              </Stack>
            </Paper>
          )}
        </Box>
      </Paper>
      
      <Stack direction="row" spacing={2} sx={{ mb: 4 }}>
        {aiProcessed && (
          <Alert severity="success" icon={<SearchIcon />} sx={{ borderRadius: 2, fontFamily: "Noto Sans, sans-serif", flex: 1, bgcolor: "white", color: "rgba(9, 135, 66, 0.79)", border: "1px solid #22c55e", "& .MuiAlert-icon": { color: "#22c55e" } }}>
            <Typography variant="body2" fontWeight="600" sx={{ fontFamily: "Noto Sans, sans-serif" }}>
              AI-Enhanced Analysis: Combined electrical calculations with database intelligence (Sorted by Price - Cheapest First)
            </Typography>
          </Alert>
        )}
      </Stack>
      
      <Grid container spacing={4} justifyContent="center">
        {recommendations.map((rec, index) => (
          <Grid item xs={12} md={6} key={index}>
            <Card elevation={3} sx={{ 
              height: "100%", 
              minHeight: "600px", // Fixed minimum height for consistency
              display: "flex", 
              flexDirection: "column", 
              borderRadius: 3, 
              position: "relative", 
              overflow: "visible", 
              border: "1px solid #e5e7eb", 
              backgroundColor: "white" 
            }}>
              <Box sx={{ position: "absolute", top: -10, left: 20, zIndex: 1 }}>
                <Chip icon={<GradeIcon sx={{ color: "white" }} />} label={index === 0 ? "Best Value" : `#${index + 1} Match`} sx={{ bgcolor: index === 0 ? "#000000" : "rgba(9, 135, 66, 0.79)", color: "white", fontWeight: "bold", fontSize: "0.85rem", fontFamily: "Noto Sans, sans-serif", "& .MuiChip-icon": { color: "white !important", fontSize: "1rem" } }} />
              </Box>
              <Box sx={{ bgcolor: "rgba(9, 135, 66, 0.79)", color: "white", p: 3, pt: 4, minHeight: "120px", display: "flex", flexDirection: "column", justifyContent: "center" }}>
                <Typography variant="h6" fontWeight="700" sx={{ mb: 1, fontFamily: "Noto Sans, sans-serif", fontSize: "1.25rem", lineHeight: 1.3, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{rec.name}</Typography>
                <Typography variant="body1" sx={{ opacity: 0.9, fontFamily: "Noto Sans, sans-serif", fontSize: "1rem" }}>{rec.brand}</Typography>
              </Box>
              <CardContent sx={{ flexGrow: 1, p: 3, display: "flex", flexDirection: "column" }}>
                <Grid container spacing={2} sx={{ mb: 3 }}>
                  <Grid item xs={6}>
                    <Paper elevation={1} sx={{ p: 2, textAlign: "center", bgcolor: "white", border: "1px solid rgba(9, 135, 66, 0.79)" }}>
                      <Typography variant="body2" color="rgba(9, 135, 66, 0.79)" sx={{ fontFamily: "Noto Sans, sans-serif" }}>Price Range</Typography>
                      <Typography variant="h6" fontWeight="600" color="rgba(9, 135, 66, 0.79)" sx={{ fontFamily: "Noto Sans, sans-serif" }}>{rec.estimatedPrice}</Typography>
                    </Paper>
                  </Grid>
                  <Grid item xs={6}>
                    <Paper elevation={1} sx={{ p: 2, textAlign: "center", bgcolor: "white", border: "1px solid #000000" }}>
                      <Typography variant="body2" color="#000000" sx={{ fontFamily: "Noto Sans, sans-serif" }}>VA Rating</Typography>
                      <Typography variant="h6" fontWeight="600" color="#000000" sx={{ fontFamily: "Noto Sans, sans-serif" }}>{rec.vaRating}</Typography>
                    </Paper>
                  </Grid>
                </Grid>
                
                {/* Battery Recommendation Text */}
                <Paper elevation={0} sx={{ p: 2, mb: 3, bgcolor: "#f9f9f9", border: "1px solid #e5e7eb", borderRadius: 2 }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
                    <BatteryChargingFullIcon sx={{ color: "#000000", fontSize: 20 }} />
                    <Typography variant="subtitle2" fontWeight="600" color="#000000" sx={{ fontFamily: "Noto Sans, sans-serif" }}>
                      Recommended Battery Configuration
                    </Typography>
                  </Box>
                  <Typography variant="body2" color="#000000" sx={{ fontFamily: "Noto Sans, sans-serif", mb: 2 }}>
                    {rec.batteryRecommendation}
                  </Typography>
                </Paper>
                
                {rec.keyFeatures && rec.keyFeatures.length > 0 && (
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="subtitle2" fontWeight="600" sx={{ mb: 1.5, color: "#000000", fontFamily: "Noto Sans, sans-serif" }}>Key Features</Typography>
                    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                      {rec.keyFeatures.slice(0, 4).map((feature, idx) => (
                        <Chip key={idx} label={feature} size="small" variant="outlined" sx={{ mb: 1, borderColor: "rgba(9, 135, 66, 0.79)", color: "rgba(9, 135, 66, 0.79)", fontFamily: "Noto Sans, sans-serif" }} />
                      ))}
                    </Stack>
                  </Box>
                )}
                
                <Paper elevation={0} sx={{ p: 2, bgcolor: "white", border: "1px solid rgba(9, 135, 66, 0.79)", borderRadius: 2, mb: 3 }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
                    <InfoIcon sx={{ color: "rgba(9, 135, 66, 0.79)", fontSize: 18 }} />
                    <Typography variant="subtitle2" fontWeight="600" color="rgba(9, 135, 66, 0.79)" sx={{ fontFamily: "Noto Sans, sans-serif" }}>Technical Analysis</Typography>
                  </Box>
                  <Typography variant="body2" color="rgba(9, 135, 66, 0.79)" sx={{ fontStyle: "italic", fontFamily: "Noto Sans, sans-serif" }}>{rec.suitability}</Typography>
                </Paper>

                {/* Spacer to push purchase links to bottom */}
                <Box sx={{ flexGrow: 1 }} />
              </CardContent>
              
              <Box sx={{ p: 3, pt: 0, mt: "auto" }}>
                <Typography variant="subtitle2" fontWeight="600" sx={{ 
                  mb: 2, 
                  color: "#000000", 
                  fontFamily: "Noto Sans, sans-serif",
                  display: "flex",
                  alignItems: "center",
                  gap: 1
                }}>
                  <LaunchIcon sx={{ fontSize: 18 }} />
                  Inverter Purchase Links
                </Typography>
                
                <Stack spacing={1.5}>
                  {rec.links?.amazon && (
                    <Button variant="contained" startIcon={<LaunchIcon />} href={rec.links.amazon} target="_blank" rel="noopener noreferrer" fullWidth sx={{ py: 1.2, fontWeight: 600, borderRadius: 2, bgcolor: "rgba(9, 135, 66, 0.79)", fontFamily: "Noto Sans, sans-serif", textTransform: "none", "&:hover": { bgcolor: "#16a34a" } }}>
                      Buy Inverter on Amazon
                    </Button>
                  )}
                  {rec.links?.flipkart && (
                    <Button variant="outlined" startIcon={<LaunchIcon />} href={rec.links.flipkart} target="_blank" rel="noopener noreferrer" fullWidth sx={{ borderRadius: 2, borderColor: "#000000", color: "#000000", fontFamily: "Noto Sans, sans-serif", textTransform: "none", "&:hover": { bgcolor: "#000000", color: "white" } }}>
                      Buy Inverter on Flipkart
                    </Button>
                  )}
                  {rec.links?.brand && (
                    <Button variant="outlined" startIcon={<LaunchIcon />} href={rec.links.brand} target="_blank" rel="noopener noreferrer" fullWidth sx={{ borderRadius: 2, borderColor: "rgba(9, 135, 66, 0.79)", color: "#rgba(9, 135, 66, 0.79)", fontFamily: "Noto Sans, sans-serif", textTransform: "none", "&:hover": { bgcolor: "rgba(9, 135, 66, 0.79)", color: "white" } }}>
                      Buy from Brand Store
                    </Button>
                  )}
                  {(!rec.links?.amazon && !rec.links?.flipkart && !rec.links?.brand) && (
                    <Alert severity="info" sx={{ fontSize: "0.8rem", bgcolor: "white", color: "#000000", border: "1px solid #e5e7eb", "& .MuiAlert-icon": { color: "#000000" } }}>
                      <Typography variant="body2" sx={{ fontFamily: "Noto Sans, sans-serif" }}>
                        Inverter purchase links will be available once database is updated
                      </Typography>
                    </Alert>
                  )}
                </Stack>
              </Box>
            </Card>
          </Grid>
        ))}
      </Grid>   
      {/* Comprehensive Battery Guide Section */}
      {(batteryAdvice || batteryRecommendations.length > 0) && (
        <Box sx={{ mt: 4 }}>
          <Typography variant="h6" fontWeight="600" sx={{ 
            mb: 3, 
            color: "#000000", 
            fontFamily: "Noto Sans, sans-serif",
            display: "flex",
            alignItems: "center",
            gap: 1
          }}>
            <BatteryChargingFullIcon sx={{ color: "rgba(9, 135, 66, 0.79)" }} />
            Compatible Battery Options 
          </Typography>
          
          {/* Match the exact Grid setup as inverter cards */}
          <Grid container spacing={4} justifyContent="center" sx={{ mt: 0 }}>
            {batteryRecommendations.map((battery, index) => (
              <Grid item xs={12} md={6} key={battery.id || index}>
                <SimpleBatteryCard 
                  battery={battery} 
                  calculatedRequirements={calculatedRequirements}
                />
              </Grid>
            ))}
          </Grid>
        </Box>
      )}
      
      {/* Comprehensive Battery Guide Section */}
{(batteryAdvice || batteryRecommendations.length > 0) && (
  <Box sx={{ mt: 4 }}>
    <Accordion
      defaultExpanded
      sx={{
        mb: 2,
        borderRadius: 2,
        border: "1px solid #e5e7eb",
        backgroundColor: "white",
      }}
    >
      <AccordionSummary
        expandIcon={<ExpandMoreIcon />}
        sx={{ bgcolor: "#f9f9f9", borderRadius: 2 }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <BatteryChargingFullIcon
            sx={{ color: "rgba(9, 135, 66, 0.79)" }}
          />
          <Typography
            variant="h6"
            fontWeight="600"
            sx={{
              color: "#000000",
              fontFamily: "Noto Sans, sans-serif",
            }}
          >
            Expert Battery Recommendations
          </Typography>
        </Box>
      </AccordionSummary>
      <AccordionDetails sx={{ p: 3 }}>
        {batteryAdvice && (
          <Box sx={{ mb: 4 }}>
            <Typography
              variant="body2"
              sx={{
                whiteSpace: "pre-line",
                fontFamily: "Noto Sans, sans-serif",
                lineHeight: 1.6,
                color: "#000000",
                bgcolor: "#f9f9f9",
                p: 2,
                borderRadius: 2,
                border: "1px solid #e5e7eb",
              }}
            >
              {batteryAdvice}
            </Typography>
          </Box>
        )}
      </AccordionDetails>
    </Accordion>
  </Box>
)}
      {recommendations.length === 0 && !loading && (
        <Paper sx={{ p: 4, textAlign: "center", bgcolor: "white", border: "1px solid #e5e7eb", borderRadius: 3 }}>
          <ErrorOutlineIcon sx={{ fontSize: 64, color: "#000000", mb: 2 }} />
          <Typography variant="h6" color="#000000" sx={{ mb: 2, fontFamily: "Noto Sans, sans-serif" }}>No Compatible Matches Found</Typography>
          <Button variant="contained" startIcon={<RefreshIcon />} onClick={handleRetry} sx={{ bgcolor: "rgba(9, 135, 66, 0.79)", fontFamily: "Noto Sans, sans-serif", "&:hover": { bgcolor: "rgba(9, 135, 66, 0.9)" } }}>Retry Analysis</Button>
        </Paper>
      )}
      
      {error && recommendations.length === 0 && !loading && (
        <Alert severity="warning" sx={{ mt: 4, borderRadius: 2, fontFamily: "Noto Sans, sans-serif", bgcolor: "white", color: "#000000", border: "1px solid #e5e7eb", "& .MuiAlert-icon": { color: "#000000" } }}>
          <Typography variant="body2" fontWeight="600" sx={{ fontFamily: "Noto Sans, sans-serif" }}>
            Please verify your internet connection and try again.
          </Typography>
        </Alert>
      )}
    </Container>
  );
};

export default Recommendation;
